/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue'
import Router from 'vue-router'
import store from './store/indexST'
import defaultRoutes from './router/default.js'
import smartTagRoutes from './router/smartTag.js'
import deepmerge from 'deepmerge';
import WyConstants from './library/wy-constants.js';

Vue.use(Router)
const router = new Router({
  routes: defaultRoutes
})
router.addRoutes(smartTagRoutes); 

router.checkUnsaveChanges = (callback) => {
  let vue = router.app;
  if(vue.unsaveDataChanages) {        
    vue.$confirm(vue.$t('warning.unsavedDataMessage'), vue.$t('warning.unsavedDataHeader'), {
      showClose: false,
      confirmButtonText: vue.$t('button.cancel'),
      cancelButtonText: vue.$t('button.confirm'),
      customClass: 'wys-message-box wy-unsaved-change-alert',
      confirmButtonClass: 'wy-grey-button',
      cancelButtonClass: 'wy-blue-button',
      type: 'warning'
    }).then(() => {
      // do nothing  
    }).catch(() => { 
      vue.unsaveDataChanages = false;
      if(callback) callback();
    });
  }
  else {
    if(callback) callback();
  }
}

router.logAmplitudeEvent = () => {
  // TODO: remove this when we retire the amplitude API
  // doing nothing
}

router.beforeEach(async (to, from, next) => {
  const isIFramePage = to.meta && to.meta.isIFramePage

  // remove events listener
  if(router.app.unregisterPageEvent) router.app.unregisterPageEvent(from.name);
  if(router.app.hideNotification) router.app.hideNotification();

  // set current page object name
  let bindModel = to.meta.bindModel;
  if(bindModel) {
    await store.dispatch('setCurrentPage', {pageName: to.name, bindModel: bindModel});
  }

  const productName = window.sessionStorage.getItem(WyConstants.SESSION_KEY_PRODUCT_NAME);
  if(to.query.sid && to.query.sid != "" && 
    (!store.getters.authUser 
      || store.getters.authUser.sessionId != to.query.sid 
      || store.getters.authUser.productName != productName
    )) {
    const authHost = to.query.host || window.parent.location.href;
    const authOptions = to.query.options;
    const sessionId = to.query.sid;
    const authUrl = window.location.href;
    await store.dispatch('c1Auth', {authHost: authHost, authOptions: authOptions, sessionId: sessionId, authUrl: authUrl, productName: WyConstants.PRODUCT_NAME_ST, isIFramePage: isIFramePage});

    if(to.name == 'smartTagDashboard') {
      next("/default");
    }
    else {
      next();
    }
  }
  else if(to.path == "/") {
    if(WyConstants.C1_DEV_LOGIN) {
      next();
    }
    else {
      next('/sessionExpired');
    }
  }
  else if(to.path == '/default') {
    next();
  }
  else if(to.path == '/sessionExpired') {
    next();
  }
  else {
    // check user session from the server
    if(!store.getters.authUser || !store.getters.authUser.token) {
      next('/sessionExpired');
    }
    else {
      // init pando
      if(!WyConstants.C1_DEV_MODE) {
        await store.dispatch('pendoInit');
      }
      
      // load glogal filters
      if(to.meta.filterAction) {
        await Promise.resolve({ 
          then: (resolve) => {
            setTimeout(async() => { 
              if(router.app.setLoading) router.app.setLoading(true);
              await store.dispatch(to.meta.filterAction, {pageName: to.meta.bindModel});          
              await store.dispatch('initFilterDataSource', {filterNames: to.meta.filterNames, fromPage: from.meta.bindModel, toPage: to.meta.bindModel});
              if(router.app.setLoading) router.app.setLoading(false);

              resolve(true);
            }, 800);
          }
        });

        // find original page name before forward to nodata page
        for(let menuItem of store.getters.config.navMenu.items) {
          if(menuItem.path==to.path) {
            store.commit("setCurrentPageInfo", {title: menuItem.label, tooltip: menuItem.tooltip});
            break;
          }
          else if(menuItem.subItems) {
            let subItem = menuItem.subItems.find(subItem => {
              return subItem.path==to.path;
            });

            if(subItem != null) {
              store.commit("setCurrentPageInfo", {title: subItem.label, subtitle: menuItem.label, tooltip: subItem.tooltip});
              break;
            }
          }
        }

        if(!await store.dispatch('checkHasFilter', {toPage: to.meta.bindModel, toPath: to.path})) {
          store.dispatch('updateDefaultRoutePath', to.path);
          // goto nodata page
          
          router.push({ path: '/nodata' });
          return;
        }
        else if(store.getters.shareState.warning && store.getters.shareState.warningMessages.length > 0) {
          router.app.showNotification("warning", null, store.getters.shareState.warningMessages, {duration: 1000000000, showClose: true, width: 500});
        }
      }

      if(store.getters.config && !store.getters.config.isInited) {
        store.getters.config.isInited = true;

        // I commmented this out.  I don't think it is neccessary anymore
        // setTimeout(() => {
        //   if (router.app.setIFrameLoading) router.app.setIFrameLoading(isIFramePage)
        // }, 800)

        if(!isIFramePage) {
          await store.dispatch('fetchAllConfigurations');
          await store.dispatch('fetchCompanyProfileSettings');
        }

        // load default config        
        let companyConfig = store.getters.config;
        let authUser = store.getters.authUser;

        if(authUser.company && authUser.company.systemProperty) {
          let productCode = authUser.company.selectedProduct.productCode;
          let systemProperty = authUser.company.systemProperty[productCode];
          if(systemProperty && Object.keys(systemProperty).length > 0) {
            companyConfig = deepmerge(companyConfig, systemProperty);
          }
        }

        // set default router page
        store.commit("updateConfig", companyConfig);
        store.commit('updateSite', {navMenu: companyConfig.navMenu});
      }

      store.dispatch('updateDefaultRoutePath', to.path);
      // alert if there are unsave data
      router.checkUnsaveChanges(next);
    }
  }
})

export default router
